import React from 'react'
import styled from 'styled-components'
import Modal, { CloseButton } from '@jarvis.oddle/core/components/Modal'
import { H4 } from '@jarvis.oddle/core/components/Typo'
import Form, { useFormContext } from '@jarvis.oddle/core/components/Form'
import StripeElement from 'src/components/molecules/StripeElement'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import { createPaymentToken, paymentTypes } from 'src/utils/payment'
import { validateCard } from 'src/utils/cardValidation'
import { isObjectEmpty } from 'src/utils/object'
import { InlineNotification, notificationStates } from '@jarvis.oddle/core/components/InlineNotification'
import useAppSelector from 'src/hooks/useAppSelector'
import { selectDetailMenuSite } from 'src/redux/meta'
import useAppDispatch from 'src/hooks/useAppDispatcher'
import {
  getAllSavedCardsAction,
  saveNewCardAction,
  setCardDefaultAction
} from 'src/redux/user'
import { makeControlledModal } from 'src/decorators/ModalController'

const StyledModalSection = styled(Modal.Section)`
  && {
    display: flex;
    flex-grow: 1;
    flex-shrink: 0;
  }
`
const StyledInlineNotification = styled(InlineNotification)`
  margin-top: 25px;
  font-weight: 700;
`
const SubmitButton = ({ text, type, disabled, loading }: {
  text: string
  type: string
  disabled: boolean
  loading: boolean
}) => {
  const { errors, values } = useFormContext()
  return (
    <Form.SubmitButton
      data-testid='addPaymentMethod.submitBtn'
      type={type}
      fullWidth
      disabled={errors.card || !values.card.complete || disabled}
      loading={loading}>
      {text}
    </Form.SubmitButton>
  )
}

const CreditCardAdditionalModal = ({
  modal,
  onSaveCard,
  isFirstCreditCard
}: {
  modal: {
    show: boolean
    onClose: () => void
    onClosed: () => void
  }
  onSaveCard: (params) => void
  isFirstCreditCard: boolean
}) => {
  const { t } = React.useContext(TranslationContext)
  const meta = useAppSelector(selectDetailMenuSite)
  const dispatch = useAppDispatch()

  const [isStripeReady, setIsStripeReady] = React.useState(false)
  const [stripe, setStripe] = React.useState(null)
  const [stripeElement, setStripeElement] = React.useState(null)
  const [loading, setLoading] = React.useState(false)

  const onSubmit = async (values, actions) => {
    setLoading(true)
    const payload = {
      values,
      selectedPaymentMethod: {
        type: paymentTypes.stripe,
        paymentMethod: null,
        useStripeSource: true
      },
      stripe,
      stripeElement,
      newStripeApi: meta.newStripeApi,
      isFullyPaidByVoucher: false,
      enableStripePaymentElement: false
    }

    const response = await createPaymentToken(payload)

    if (response.tokenError && stripeElement) {
      stripeElement.focus()
      actions.setFieldError('card', t(response.tokenError.message))
    }
    if (response.token) {
      const paymentMethod = JSON.parse(response.token)
      try {
        const responseSaveCard = await dispatch(saveNewCardAction({ paymentMethodId: paymentMethod.paymentMethodId })).unwrap()
        if (responseSaveCard.success) {
          if (isFirstCreditCard) {
            const listSavedCard = await dispatch(getAllSavedCardsAction())
            await dispatch(setCardDefaultAction({ paymentMethodId: listSavedCard?.payload?.paymentMethods?.[0]?.id }))
          }
          onSaveCard(responseSaveCard)
          modal.onClose()
        }
      } catch (error) {
        stripeElement.focus()
        actions.setFieldValue('card', {
          ...values.card,
          error: {
            message: isObjectEmpty(error) ? t('Oops! There seems to be a problem. Please try again later.') : t(error.message)
          }
        }, true)
      }
    }
    actions.setSubmitting(false)
    setLoading(false)
  }

  const onValidate = ({ cardName, card }) => validateCard({
    t,
    cardName,
    card,
    selectedPaymentMethod: {
      type: paymentTypes.stripe,
      paymentMethod: null,
      useStripeSource: true
    } as Oms.V4.PaymentMethodData
  })

  return (
    <Modal {...modal}>
      <Form
        initialValues={{ cardName: '', card: {} }}
        onValidate={onValidate}
        onSubmit={onSubmit}
        disableAutocomplete>
        <Modal.FlexContainer>
          <Modal.Header>
            <H4 margin='none' bold>{t('Add New Card')}</H4>
            <CloseButton onClick={modal.onClose} />
          </Modal.Header>
          <Modal.Body>
            <StyledModalSection>
              <StripeElement
                type={null}
                onStripeReady={(element) => {
                  setStripeElement(element)
                  setIsStripeReady(true)
                }}
                onStripeLoaded={instance => setStripe(instance)}
                apiKey={meta?.stripeToken}
                title={t('Add New Card')}
              />
              <StyledInlineNotification
                lightTheme
                state={notificationStates.informative}>
                {t('Your payment details are safe. All payment details are encrypted and securely processed in compliance with international payment card security standards. We do not have access to your payment details.')}
              </StyledInlineNotification>
            </StyledModalSection>
          </Modal.Body>
          <Modal.Footer>
            <SubmitButton
              type='submit'
              text={t('Save')}
              disabled={!isStripeReady || loading}
              loading={loading}
            />
          </Modal.Footer>
        </Modal.FlexContainer>
      </Form>
    </Modal>
  )
}

export default makeControlledModal(CreditCardAdditionalModal, 'CreditCardAdditionalModal')
